import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Check, Download, Loader2, Printer, ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@akuna/ui";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useMedicalRecordExport } from "@/hooks/useMedicalRecordExport";
import { useProntuarioTimeline } from "@/hooks/useProntuarioTimeline";
import { buildProntuarioPdf, downloadPdf } from "@/lib/prontuario/export";
import { supabase } from "@/integrations/supabase/client";
import { generateUUID } from "@/lib/utils/uuid";
import { useUserA1Certificate } from "@/hooks/useUserA1Certificate";
import { useSignPdf } from "@/hooks/useSignPdf";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  medicalRecordId: string;
  prontuarioId?: string;
}

export function MedicalRecordExportDialog({
  open,
  onOpenChange,
  medicalRecordId,
  prontuarioId,
}: Props) {
  const { t } = useTranslation("medicalRecords");
  const { data, isLoading, isError, error, refetch } =
    useMedicalRecordExport(medicalRecordId);
  const { data: timeline } = useProntuarioTimeline(medicalRecordId);
  const { data: a1Certificate } = useUserA1Certificate();
  const hasCertificate = Boolean(a1Certificate);
  const signPdf = useSignPdf();

  const [isExporting, setIsExporting] = useState(false);
  const [lastExport, setLastExport] = useState<{
    exportId: string;
    hash: string;
    pdfBytes: Uint8Array;
  } | null>(null);
  const [signedStoragePath, setSignedStoragePath] = useState<string | null>(null);
  const [signedAt, setSignedAt] = useState<string | null>(null);

  const toBase64 = (bytes: Uint8Array) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = typeof reader.result === "string" ? reader.result : "";
        const base64 = result.split(",")[1] ?? "";
        resolve(base64);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(new Blob([bytes as BlobPart], { type: "application/pdf" }));
    });

  const handleExportPdf = async () => {
    if (!data?.record) return;
    try {
      setIsExporting(true);
      setSignedStoragePath(null);
      setSignedAt(null);
      const exportId = generateUUID();
      const verificationUrl = `${window.location.origin}/verificar?export_id=${exportId}`;
      const { pdfBytes, hash } = await buildProntuarioPdf({
        record: data.record,
        vaccinations: data.vaccinations,
        examResults: data.examResults,
        prescriptions: data.prescriptions,
        versions: timeline?.versions,
        events: timeline?.events,
        exportId,
        verificationUrl,
      });
      const pdfBase64 = await toBase64(pdfBytes);
      const { data: exportData, error: exportError } = await supabase.functions.invoke(
        "prontuario-export",
        {
          body: {
            export_id: exportId,
            medical_record_id: medicalRecordId,
            pdf_base64: pdfBase64,
            template_version: "prontuario-v1",
            app_version: import.meta.env.VITE_APP_VERSION ?? null,
          },
        },
      );
      if (exportError) {
        throw new Error(exportError.message ?? t("verifyExport.invokeError"));
      }
      const responseExportId = exportData?.export_id ?? exportId;
      const responseHash = exportData?.pdf_sha256 ?? hash;
      setLastExport({ exportId: responseExportId, hash: responseHash, pdfBytes });
      downloadPdf(pdfBytes, `Prontuario_${medicalRecordId}.pdf`);
    } catch (err) {
      console.error("Erro ao exportar prontuário:", err);
    } finally {
      setIsExporting(false);
    }
  };

  const handleSignExport = async () => {
    if (!lastExport) return;
    try {
      const result = await signPdf.mutateAsync({
        resource_type: "prontuario_export",
        resource_id: lastExport.exportId,
        prontuario_id: prontuarioId,
      });
      setSignedStoragePath(result.storage_path);
      setSignedAt(new Date().toISOString());
      if (result.signed_pdf_base64) {
        const signed = Uint8Array.from(atob(result.signed_pdf_base64), (c) => c.charCodeAt(0));
        downloadPdf(signed, `Prontuario_assinado_${medicalRecordId}.pdf`);
      }
      toast.success(t("exportDialog.signSuccess", { defaultValue: "Prontuário assinado com sucesso!" }));
    } catch (err) {
      console.error("Erro ao assinar prontuário:", err);
    }
  };

  const handleDownloadSigned = async () => {
    if (!signedStoragePath) return;
    try {
      const { data: urlData, error: urlErr } = await supabase.storage
        .from("signed-pdfs")
        .createSignedUrl(signedStoragePath, 60);
      if (urlErr || !urlData?.signedUrl) throw urlErr ?? new Error("url_failed");
      window.open(urlData.signedUrl, "_blank");
    } catch (err) {
      console.error(err);
      toast.error(t("exportDialog.downloadSignedError", { defaultValue: "Erro ao baixar PDF assinado" }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="text-left">{t("exportDialog.title")}</DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-120px)]">
          <div className="mt-4 flex flex-col gap-4 pr-4">
            <Button
              onClick={handleExportPdf}
              disabled={isLoading || !data?.record || isExporting}
              className="w-full"
            >
              {isLoading || isExporting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {isExporting ? t("exportDialog.generatingPdf") : t("exportDialog.loading")}
                </>
              ) : (
                <>
                  <Printer className="h-4 w-4 mr-2" />
                  {t("exportDialog.confirm")}
                </>
              )}
            </Button>

            {isLoading ? (
              <div className="flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : isError ? (
              <div className="flex flex-col gap-3 text-sm">
                <p className="text-destructive">
                  {t("exportDialog.loadError")}
                </p>
                <p className="text-muted-foreground">
                  {error instanceof Error ? error.message : t("exportDialog.unknownError")}
                </p>
                <Button variant="outline" onClick={() => refetch()}>
                  {t("exportDialog.retry")}
                </Button>
              </div>
            ) : data ? (
              <div className="">
                <p className="text-sm text-muted-foreground">
                  {t("exportDialog.description")}
                </p>
                {lastExport ? (
                  <div className="mt-4 text-sm text-muted-foreground space-y-2">
                    <div>
                      <p className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                        {t("exportDialog.exportId")}:
                      </p>
                      <p className="font-mono text-xs break-all mt-1">{lastExport.exportId}</p>
                    </div>
                    <div>
                      <p className="flex items-center gap-2">
                        {t("exportDialog.pdfHash")}:
                      </p>
                      <p className="font-mono text-xs break-all mt-1">{lastExport.hash}</p>
                    </div>

                    {/* Sign exported PDF with A1 certificate */}
                    {hasCertificate && !signedAt && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-2"
                        onClick={handleSignExport}
                        disabled={signPdf.isPending}
                      >
                        {signPdf.isPending ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <ShieldCheck className="mr-2 h-4 w-4" />
                        )}
                        {signPdf.isPending
                          ? t("exportDialog.signing", { defaultValue: "Assinando..." })
                          : t("exportDialog.signButton", { defaultValue: "Assinar Digitalmente" })}
                      </Button>
                    )}

                    {/* Signed state */}
                    {signedAt && (
                      <div className="flex flex-col gap-2 mt-2">
                        <Badge variant="default" className="flex items-center gap-1 w-fit bg-green-600 text-white hover:bg-green-700">
                          <ShieldCheck className="h-3.5 w-3.5" />
                          {t("exportDialog.signed", { defaultValue: "Assinado digitalmente" })}
                          <span className="ml-1 opacity-75 text-xs">
                            · {new Date(signedAt).toLocaleDateString("pt-BR")}
                          </span>
                        </Badge>
                        {signedStoragePath && (
                          <Button variant="outline" size="sm" onClick={handleDownloadSigned}>
                            <Download className="mr-2 h-4 w-4" />
                            {t("exportDialog.downloadSigned", { defaultValue: "Baixar PDF assinado" })}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ) : null}
                <Button variant="link" size="sm" asChild className="px-0 mt-2">
                  <Link target="_blank" to={`/verificar?export_id=${lastExport?.exportId ?? ""}`}>
                    {t("exportDialog.verifyPage")}
                  </Link>
                </Button>
              </div>
            ) : null}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
